import type { StockClipSummary } from '@borradh-workspace/api-client/types';
import { useRef } from 'react';

import { cn } from '@/lib/utils';

interface StockClipPlayerProps {
  clip: StockClipSummary;
  /** Start playing on load, muted. For the clip being reviewed. */
  autoPlay?: boolean;
  className?: string;
}

/**
 * Plays only the part of a stock clip that videos actually use (its trim
 * window), on a loop, so the reviewer judges exactly what clinics will see.
 */
export function StockClipPlayer({
  clip,
  autoPlay = false,
  className,
}: StockClipPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const trimIn = clip.trimInMs != null ? clip.trimInMs / 1000 : null;
  const trimOut = clip.trimOutMs != null ? clip.trimOutMs / 1000 : null;
  const frame = cn(
    'aspect-video w-full rounded-lg bg-black object-contain',
    className
  );

  if (clip.mediaType === 'image') {
    return (
      <img
        src={clip.previewUrl}
        alt={clip.visualDescription ?? 'Stock image'}
        className={frame}
      />
    );
  }

  return (
    <video
      ref={videoRef}
      src={clip.previewUrl}
      controls
      muted
      playsInline
      autoPlay={autoPlay}
      preload="metadata"
      className={frame}
      onLoadedMetadata={() => {
        if (trimIn != null && videoRef.current) {
          videoRef.current.currentTime = trimIn;
        }
      }}
      onTimeUpdate={() => {
        const v = videoRef.current;
        if (v && trimIn != null && trimOut != null && v.currentTime > trimOut) {
          v.currentTime = trimIn;
        }
      }}
      // Loop by hand rather than with `loop`, which would restart from 0s
      // instead of the start of the trim window.
      onEnded={() => {
        const v = videoRef.current;
        if (!v) return;
        v.currentTime = trimIn ?? 0;
        void v.play();
      }}
    />
  );
}

/** "Plays 2.0s to 9.5s" — the part of the clip used in videos. */
export function trimWindowLabel(clip: StockClipSummary): string {
  if (clip.trimInMs == null || clip.trimOutMs == null) {
    return 'Plays the whole clip.';
  }
  const from = (clip.trimInMs / 1000).toFixed(1);
  const to = (clip.trimOutMs / 1000).toFixed(1);
  return `Plays ${from}s to ${to}s, the part used in videos.`;
}
